import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { demoSupplierSession } from "./demo-data";
import { isDemoMode, usesLocalData } from "./demo-mode";
import { mockAdminSession, mockSupplierSession } from "./mock-data";
import type { UserSession } from "../types";

interface AuthValue {
  session: UserSession | null;
  requiresNewPassword: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);
const useMock = usesLocalData;

function cognitoPool(): CognitoUserPool {
  return new CognitoUserPool({
    UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
    ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
  });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<UserSession | null>(() => {
    if (!useMock) return null;
    if (isDemoMode) return demoSupplierSession;
    return sessionStorage.getItem("mock-role") === "admin"
      ? mockAdminSession
      : null;
  });
  const [pendingPasswordUser, setPendingPasswordUser] =
    useState<CognitoUser | null>(null);

  function establishSession(result: CognitoUserSession, username: string) {
    const payload = result.getIdToken().decodePayload();
    const groups = (payload["cognito:groups"] as string[] | undefined) ?? [];
    setSession({
      accessToken: result.getIdToken().getJwtToken(),
      displayName: String(payload.name ?? payload.email ?? username),
      email: String(payload.email ?? username),
      role: groups.includes("bai_admin") ? "bai_admin" : "supplier_updater",
      supplierOrganizationId: payload["custom:supplier_org_id"] as
        string | undefined,
    });
  }

  const value = useMemo<AuthValue>(
    () => ({
      session,
      requiresNewPassword: pendingPasswordUser !== null,
      async signIn(username, password) {
        if (useMock) {
          const next = isDemoMode
            ? demoSupplierSession
            : username.toLowerCase().includes("admin")
              ? mockAdminSession
              : mockSupplierSession;
          sessionStorage.setItem(
            "mock-role",
            next.role === "bai_admin" ? "admin" : "supplier",
          );
          setSession(next);
          return;
        }
        await new Promise<void>((resolve, reject) => {
          const user = new CognitoUser({
            Username: username,
            Pool: cognitoPool(),
          });
          user.authenticateUser(
            new AuthenticationDetails({
              Username: username,
              Password: password,
            }),
            {
              onSuccess(result) {
                establishSession(result, username);
                resolve();
              },
              newPasswordRequired() {
                setPendingPasswordUser(user);
                resolve();
              },
              onFailure: reject,
            },
          );
        });
      },
      async completeNewPassword(newPassword) {
        if (useMock) return;
        if (!pendingPasswordUser) {
          throw new Error("No password setup is pending.");
        }
        await new Promise<void>((resolve, reject) => {
          pendingPasswordUser.completeNewPasswordChallenge(
            newPassword,
            {},
            {
              onSuccess(result) {
                establishSession(result, pendingPasswordUser.getUsername());
                setPendingPasswordUser(null);
                resolve();
              },
              onFailure: reject,
            },
          );
        });
      },
      signOut() {
        if (!useMock) cognitoPool().getCurrentUser()?.signOut();
        sessionStorage.removeItem("mock-role");
        setPendingPasswordUser(null);
        setSession(null);
      },
    }),
    [pendingPasswordUser, session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
